<template>
  <div class="weight-history-page">
    <div class="weight-history-page__toolbar">
      <div class="weight-history__filters">
        <Dropdown :title="selectedPeriod.title" :items="periodDropdownOptions" />
        <Dropdown :title="selectedResolution.title" :items="resolutionDropdownOptions" />
      </div>
    </div>

    <CRow>
      <CCol lg="8" class="mb-4">
        <WeightHistoryChart :averages="averages2" :selectedResolution="selectedResolution" />
      </CCol>

      <CCol lg="4" class="mb-4">
        <WeightHistoryDiffChart :averages="averages2" :selectedResolution="selectedResolution" />
      </CCol>
    </CRow>

    <CCard class="weight-history-table">
      <CCardBody class="p-0">
        <div class="weight-history-table__header">
          <div>Datum</div>
          <div>Tezina</div>
          <div>Promjena</div>
        </div>
        <div v-for="(item, index) in groupedWeights2" :key="item.key" class="weight-history-table__group">
          <button
            type="button"
            class="weight-history-table__row"
            :class="{ 'weight-history-table__row--striped': index % 2 === 0 }"
            @click="toggle(item)"
          >
            <div class="weight-history-table__title">{{ item.title }}</div>
            <div class="weight-history-table__value">{{ item.value || "-" }}</div>
            <div class="weight-history-table__diff">
              <span
                v-if="item.diffToLastAverage !== null && item.diffToLastAverage !== undefined"
                :class="weightDiffClass(item.diffToLastAverage)"
              >
                {{ item.diffToLastAverage }}
              </span>
              <span v-else class="weight-history-table__diff--empty">-</span>
              <CIcon
                v-if="selectedResolution.key !== 'daily'"
                :name="item.isExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'"
                class="weight-history-table__chevron"
              />
            </div>
          </button>

          <CCollapse :show="item.isExpanded">
            <div class="weight-history-table__details">
              <div v-for="item2 in item.items" :key="item2.date" class="weight-history-table__detail-row">
                <div>{{ item2.date }}</div>
                <div>{{ item2.weight || "-" }}</div>
                <div></div>
              </div>
            </div>
          </CCollapse>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import WeightHistoryChart from "./WeightHistoryChart";
import WeightHistoryDiffChart from "./WeightHistoryDiffChart";
import Dropdown from "./WeightHistoryDropdown";
import moment from "moment";

const currentYear = new Date().getFullYear();
const getChartXAxisDateFormat = (date) => moment(date).format("DD.MM" + (moment(date).format("YYYY") == currentYear ? "" : ".YY"));

const resolutionDropdownOptions = [
  {
    key: "daily",
    title: "Dnevno",
    isSelected: false,
    chartTitle: "Danas",
    chartSubTitle: "Dnevna vaganja",
    diffChartTitle: "Današnja razlika",
    diffChartSubtitle: "Dnevne razlike"
  },
  {
    key: "weekly",
    title: "Tjedno",
    isSelected: true,
    chartTitle: "Tjedni prosjek",
    chartSubTitle: "Tjedni prosjeci",
    diffChartTitle: "Tjedna razlika",
    diffChartSubtitle: "Tjedne razlike"
  },
  {
    key: "monthly",
    title: "Mjesečno",
    isSelected: false,
    chartTitle: "Mjesečni prosjek",
    chartSubTitle: "Mjesečni prosjeci",
    diffChartTitle: "Mjesečna razlika",
    diffChartSubtitle: "Mjesečne razlike"
  }
];

const dateIsSunday = (date) => date.locale("en").weekday() === 0;

const getDaysBetweenDates = (from, to) => {
  const dates = [];
  while (from.isSameOrBefore(to)) {
    dates.push(from);
    from = moment(from).add(1, "days");
  }
  return dates;
};

const getAllDaysWeights = (weights) => {
  const fromDate = weights[0].date;
  const toDate = moment().startOf("day");
  return getDaysBetweenDates(fromDate, toDate).map((date) => {
    const weightValue =
      weights
        .filter((weightEntry) => date.isSame(weightEntry.date))
        .map((weightEntry) => weightEntry.value)
        .pop() ?? "";

    return {
      date,
      weight: weightValue > 0 ? weightValue : "",
    };
  });
};

export default {
  components: {
    WeightHistoryChart,
    WeightHistoryDiffChart,
    Dropdown,
  },
  methods: {
    toggle(item) {
      if (this.selectedResolution.key !== "daily") {
        item.isExpanded = !item.isExpanded;
        this.$forceUpdate();
      }
    },
    weightDiffClass(diff) {
      return Number(diff) <= 0 ? "weight-history-table__diff--good" : "weight-history-table__diff--bad";
    },
  },
  data() {
    return {
      resolutionDropdownOptions,
      periodDropdownOptions: [
        { key: "last3Months", title: "3 mjeseca", isSelected: true },
        { key: "all", title: "Sve", isSelected: false },
      ],
    };
  },
  computed: {
    weihghtsData() {
      let weekWeights = [];
      let groupedWeights = [];
      let weightsGroup = [];
      const allDaysWeights = getAllDaysWeights(this.$store.state.weightHistory);
      for (let i = 0; i < allDaysWeights.length; i += 1) {
        const dayWeight = allDaysWeights[i];

        if (dayWeight.weight) {
          weekWeights.push(dayWeight.weight);
        }
        weightsGroup.push(dayWeight);

        if (
          (this.selectedResolution.key !== "daily" && i === allDaysWeights.length - 1) ||
          (this.selectedResolution.key === "weekly" && dateIsSunday(dayWeight.date)) ||
          (this.selectedResolution.key === "monthly" && allDaysWeights[i + 1].date.format("D") === "1")
        ) {
          const getAverage = (arr) => arr.reduce((previous, current) => previous + current, 0) / arr.length;

          const average = weekWeights.length > 0 ? getAverage(weekWeights).toFixed(1) : null;

          const groupedWeightsItems = weightsGroup
            .map((weightEntry) => {
              return { date: weightEntry.date.format("YYYY-MM-DD"), weight: weightEntry.weight };
            })
            .reverse();

          let lastGroupedWeight = null;
          for (let j = groupedWeights.length - 1; j >= 0; j -= 1) {
            if (groupedWeights[j].value) {
              lastGroupedWeight = groupedWeights[j].value;
              break;
            }
          }

          let diffToLastAverage = average && lastGroupedWeight ? average - lastGroupedWeight : null;
          if (diffToLastAverage) {
            diffToLastAverage = `${diffToLastAverage > 0 ? "+" : ""}${diffToLastAverage.toFixed(1)}`;
          }

          groupedWeights.push({
            title:
              this.selectedResolution.key === "weekly"
                ? `tjedan ${weightsGroup[weightsGroup.length - 1].date.format("YYYY-MM")}`
                : `mjesec ${weightsGroup[weightsGroup.length - 1].date.format("YYYY-MM")}`,
            isExpanded: false,
            rawDate: weightsGroup[0].date,
            date: groupedWeightsItems[0].date,
            value: average,
            diffToLastAverage,
            items: groupedWeightsItems,
            key: getChartXAxisDateFormat(groupedWeightsItems[0].date),
          });
          weekWeights = [];
          weightsGroup = [];
        }
      }

      if (this.selectedResolution.key === "daily") {
        groupedWeights = weightsGroup.map((weightEntry, index) => {
          let diffToLastAverage = null;
          if (index > 0 && weightEntry.weight && weightsGroup[index - 1].weight) {
            const diff = weightEntry.weight - weightsGroup[index - 1].weight;
            diffToLastAverage = `${diff > 0 ? "+" : ""}${diff.toFixed(1)}`;
          }

          return {
            title: weightEntry.date.format("YYYY-MM-DD"),
            isExpanded: false,
            rawDate: weightEntry.date,
            date: weightEntry.date.format("YYYY-MM-DD"),
            value: weightEntry.weight,
            items: [],
            key: getChartXAxisDateFormat(weightEntry.date),
            diffToLastAverage,
          };
        });
      }
      return {
        groupedWeights: groupedWeights.reverse(),
      };
    },

    selectedPeriod() {
      return this.periodDropdownOptions.find((period) => period.isSelected);
    },
    selectedResolution() {
      return this.resolutionDropdownOptions.find((resolution) => resolution.isSelected);
    },
    averages2() {
      return this.groupedWeights2.map((entry) => entry).reverse();
    },
    groupedWeights2() {
      const threeMonthsAgo = moment().subtract(3, "months");
      return this.weihghtsData.groupedWeights.filter((entry) => (this.selectedPeriod.key === "last3Months" ? entry.rawDate > threeMonthsAgo : true));
    },
  },
};
</script>

<style>
.weight-history-page {
  display: grid;
  gap: 1.5rem;
}

.weight-history-page__toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.weight-history__filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.weight-history-table {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: none;
  overflow: hidden;
}

.weight-history-table__header,
.weight-history-table__row,
.weight-history-table__detail-row {
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(90px, 0.8fr) minmax(110px, 0.9fr);
  gap: 1rem;
  align-items: center;
}

.weight-history-table__header {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

button.weight-history-table__row {
  width: 100%;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #eef2f7 !important;
  background: #ffffff !important;
  border: 0 !important;
  border-radius: 0 !important;
  color: #0f172a !important;
  text-align: left;
  min-height: 0 !important;
  box-shadow: none !important;
  transform: none !important;
  justify-content: stretch;
}

.weight-history-table__row--striped {
  background: #fcfdfd;
}

button.weight-history-table__row:hover,
button.weight-history-table__row:focus {
  background: #f8fafc !important;
  color: #0f172a !important;
  box-shadow: none !important;
  transform: none !important;
}

.weight-history-table__title {
  font-weight: 600;
}

.weight-history-table__value {
  font-weight: 700;
}

.weight-history-table__diff {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
}

.weight-history-table__diff--good {
  color: #059669;
  font-weight: 700;
}

.weight-history-table__diff--bad {
  color: #dc2626;
  font-weight: 700;
}

.weight-history-table__diff--empty {
  color: #94a3b8;
}

.weight-history-table__chevron {
  color: #94a3b8;
  flex-shrink: 0;
}

.weight-history-table__details {
  padding: 0 1.5rem 0.75rem;
  background: #f8fafc;
}

.weight-history-table__detail-row {
  padding: 0.75rem 0;
  border-top: 1px solid #e5e7eb;
  color: #475569;
  font-size: 0.95rem;
}

@media (max-width: 991px) {
  .weight-history-page__toolbar {
    align-items: flex-start;
  }
}

@media (max-width: 767px) {
  .weight-history-table__header,
  .weight-history-table__row,
  .weight-history-table__detail-row {
    grid-template-columns: minmax(0, 1.2fr) minmax(72px, 0.7fr) minmax(96px, 0.8fr);
    gap: 0.75rem;
  }

  .weight-history-table__header,
  .weight-history-table__row,
  .weight-history-table__details {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
